import axios from "axios";
import { toast } from 'react-toastify';

const BASE_URL = 'https://anpe-5977c86345cd.herokuapp.com';  // Assurez-vous que c'est la bonne URL


// https://anpe-5977c86345cd.herokuapp.com
// Fonction pour vérifier si un token JWT est expiré
const isTokenExpired = (token) => {
    if (!token) return true;

    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        const currentTime = Date.now() / 1000;
        return payload.exp < currentTime;
    } catch (error) {
        console.error('Erreur lors de la vérification du token:', error);
        return true;
    }
};

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Fonction pour rafraîchir le token
const refreshToken = async (refreshToken) => {
    try {
        const response = await axios.post(`${BASE_URL}/api/token/refresh/`, {
            refresh: refreshToken
        });

        if (response.data.access) {
            const tokensString = localStorage.getItem('auth_tokens');
            if (tokensString) {
                const tokens = JSON.parse(tokensString);
                const newTokens = {
                    ...tokens,
                    access: response.data.access,
                    // Si un nouveau refresh token est retourné, on le stocke aussi
                    refresh: response.data.refresh || tokens.refresh
                };
                localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
                return response.data.access;
            }
        }
        return null;
    } catch (error) {
        console.error('Erreur lors du rafraîchissement du token:', error);
        // En cas d'erreur, on nettoie les tokens stockés
        localStorage.removeItem('auth_tokens');
        return null;
    }
};

// URLs qui n'ont pas besoin de token d'authentification
const NO_AUTH_URLS = [
    '/utilisateur/signin/',
    '/utilisateur/register/',
    '/api/token/',
    '/utilisateur/password-reset/check-email/',
    '/utilisateur/password-reset/send-otp/',
    '/utilisateur/password-reset/verify-otp/',
    '/utilisateur/password-reset/reset/',
    '/demandeur/register-referentiels/',
];

const isPublicUrl = (url) => NO_AUTH_URLS.some(publicUrl => url.includes(publicUrl));

// Vérifier si l'URL actuelle est la page de connexion ou d'inscription
const isAuthPage = () => {
    const path = window.location.pathname;
    return [
        '/connexion',
        '/inscription',
        '/mot-de-passe-oublie',
        '/verification-otp',
        '/reinitialiser-mot-de-passe',
    ].includes(path);
};

// Intercepteur pour ajouter le token aux requêtes
api.interceptors.request.use(
    async (config) => {
        // Ne pas ajouter de token pour les routes publiques
        if (isPublicUrl(config.url)) {
            return config;
        }

        try {
            const tokensString = localStorage.getItem('auth_tokens');
            if (tokensString) {
                const tokens = JSON.parse(tokensString);

                // Si le token d'accès est expiré, on essaie de le rafraîchir
                if (isTokenExpired(tokens.access)) {
                    const newAccessToken = await refreshToken(tokens.refresh);
                    if (newAccessToken) {
                        config.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    } else {
                        // Si le rafraîchissement échoue, on déconnecte l'utilisateur
                        localStorage.removeItem('auth_tokens');
                        localStorage.removeItem('credentials');

                        // Rediriger vers la page de connexion seulement si on n'y est pas déjà
                        if (!isAuthPage()) {
                            window.location.href = '/connexion';
                            toast.error("Votre session a expiré. Veuillez vous reconnecter.");
                        }
                        return Promise.reject(new Error('Token refresh failed'));
                    }
                } else {
                    config.headers['Authorization'] = `Bearer ${tokens.access}`;
                }
            }
            return config;
        } catch (error) {
            return Promise.reject(error);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Si c'est une route publique, laisser le composant gérer l'erreur directement
        if (isPublicUrl(originalRequest.url)) {
            return Promise.reject(error);
        }

        // Vérifier si c'est une erreur d'authentification (401)
        if (error.response?.status === 401) {
            // Si c'est une erreur de token expiré et que la requête n'a pas déjà été retentée
            if (!originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    const tokensString = localStorage.getItem('auth_tokens');
                    if (tokensString) {
                        const tokens = JSON.parse(tokensString);
                        const newAccessToken = await refreshToken(tokens.refresh);

                        if (newAccessToken) {
                            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                            return api(originalRequest);
                        }
                    }
                } catch (refreshError) {
                    console.error('Erreur lors du rafraîchissement du token:', refreshError);
                }

                // Si le rafraîchissement échoue, on déconnecte l'utilisateur
                localStorage.removeItem('auth_tokens');
                localStorage.removeItem('credentials');

                // Rediriger vers la page de connexion seulement si on n'y est pas déjà
                if (!isAuthPage()) {
                    window.location.href = '/connexion';
                    toast.error("Votre session a expiré. Veuillez vous reconnecter.");
                }
            }
        }

        return Promise.reject(error);
    }
);

export default api;